import { useMemo, useState } from 'react';
import { cn } from '../lib/utils';

export type SortDir = 'asc' | 'desc';

// Extracted from BoardPage so the service board gets the identical filter and
// sort behaviour instead of a second copy that drifts. Same reason
// rowGridTemplate moved out of JobRow: two copies of a shared widget diverge,
// and the office notices when one board filters differently from the other.

/**
 * The per-column sort and filter menu.
 *
 * Sort at the top, a contains box, then the list of values actually present in
 * this column with checkboxes. Nothing needs applying; every change lands
 * immediately, which is how a spreadsheet behaves and saves a click Andrea
 * would otherwise make hundreds of times a week.
 */
export function ColumnMenu({
    label,
    x,
    y,
    facets,
    sortDir,
    text,
    selected,
    onSort,
    onText,
    onSelected,
    onClose,
}: {
    label: string;
    x: number;
    y: number;
    facets: string[];
    sortDir: SortDir | null;
    text: string;
    selected: string[];
    onSort: (dir: SortDir | null) => void;
    onText: (v: string) => void;
    onSelected: (vals: string[]) => void;
    onClose: () => void;
}) {
    const [valueSearch, setValueSearch] = useState('');
    const selectedSet = useMemo(() => new Set(selected), [selected]);

    const shown = useMemo(() => {
        const q = valueSearch.trim().toLowerCase();
        if (!q) return facets;
        return facets.filter((f) => f.toLowerCase().includes(q));
    }, [facets, valueSearch]);

    // "All" is the absence of a filter, not every box ticked, so clearing the
    // list is what un-filters the column.
    const allChecked = selected.length === 0;

    function toggle(v: string) {
        const next = new Set(selectedSet);
        if (next.has(v)) next.delete(v);
        else next.add(v);
        onSelected([...next]);
    }

    return (
        <>
            <div className="fixed inset-0 z-40" onClick={onClose} />
            <div
                role="dialog"
                aria-label={`${label} sort and filter`}
                style={{ position: 'fixed', left: x, top: y, width: 288 }}
                className="z-50 bg-white dark:bg-graphite-900 border border-graphite-300 dark:border-graphite-700 rounded shadow-card-md overflow-hidden"
            >
                <div className="px-3 py-2 border-b border-graphite-200 dark:border-graphite-800 flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-graphite-600 dark:text-graphite-400 truncate">
                        {label}
                    </span>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="text-graphite-500 hover:text-graphite-900 dark:hover:text-graphite-200 -m-1 p-1"
                    >
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                            <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.5" />
                        </svg>
                    </button>
                </div>

                <div className="px-2 py-2 border-b border-graphite-200 dark:border-graphite-800 flex flex-col gap-1">
                    <MenuAction active={sortDir === 'asc'} onClick={() => onSort(sortDir === 'asc' ? null : 'asc')}>
                        Sort A to Z
                    </MenuAction>
                    <MenuAction active={sortDir === 'desc'} onClick={() => onSort(sortDir === 'desc' ? null : 'desc')}>
                        Sort Z to A
                    </MenuAction>
                </div>

                <div className="px-2 py-2 border-b border-graphite-200 dark:border-graphite-800">
                    <input
                        type="text"
                        value={text}
                        onChange={(e) => onText(e.target.value)}
                        placeholder={`${label} contains...`}
                        className="w-full bg-graphite-50 dark:bg-graphite-950 border border-graphite-300 dark:border-graphite-700 rounded px-2 py-1.5 font-mono text-[11px] text-graphite-900 dark:text-graphite-100 placeholder-graphite-400 dark:placeholder-graphite-600 focus:border-signal-500 focus:outline-none"
                    />
                </div>

                {facets.length > 0 ? (
                    <>
                        {facets.length > 8 ? (
                            <div className="px-2 pt-2">
                                <input
                                    type="text"
                                    value={valueSearch}
                                    onChange={(e) => setValueSearch(e.target.value)}
                                    placeholder="Find a value..."
                                    className="w-full bg-graphite-50 dark:bg-graphite-950 border border-graphite-300 dark:border-graphite-700 rounded px-2 py-1 font-mono text-[11px] text-graphite-900 dark:text-graphite-100 placeholder-graphite-400 dark:placeholder-graphite-600 focus:border-signal-500 focus:outline-none"
                                />
                            </div>
                        ) : null}
                        <div className="max-h-56 overflow-auto px-2 py-2 flex flex-col gap-0.5">
                            <label className="flex items-center gap-2 px-1.5 py-1 rounded cursor-pointer select-none hover:bg-graphite-100 dark:hover:bg-graphite-800">
                                <input
                                    type="checkbox"
                                    checked={allChecked}
                                    onChange={() => onSelected([])}
                                    className="accent-signal-500"
                                />
                                <span className="text-xs text-graphite-800 dark:text-graphite-200">All</span>
                            </label>
                            {shown.map((f) => (
                                <label
                                    key={f}
                                    className="flex items-center gap-2 px-1.5 py-1 rounded cursor-pointer select-none hover:bg-graphite-100 dark:hover:bg-graphite-800"
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedSet.has(f)}
                                        onChange={() => toggle(f)}
                                        className="accent-signal-500"
                                    />
                                    <span className="text-xs text-graphite-800 dark:text-graphite-200 truncate" title={f}>
                                        {f}
                                    </span>
                                </label>
                            ))}
                            {shown.length === 0 ? (
                                <p className="px-1.5 py-1 text-xs text-graphite-500">No values match.</p>
                            ) : null}
                        </div>
                    </>
                ) : null}

                {(text || selected.length > 0 || sortDir) ? (
                    <div className="px-2 py-2 border-t border-graphite-200 dark:border-graphite-800">
                        <button
                            type="button"
                            onClick={() => {
                                onText('');
                                onSelected([]);
                                onSort(null);
                                setValueSearch('');
                            }}
                            className="w-full px-2 py-1.5 rounded font-mono text-[10px] uppercase tracking-wider text-graphite-600 dark:text-graphite-400 hover:bg-graphite-100 dark:hover:bg-graphite-800 transition-colors"
                        >
                            Clear this column
                        </button>
                    </div>
                ) : null}
            </div>
        </>
    );
}

export function MenuAction({
    children,
    active,
    onClick,
}: {
    children: React.ReactNode;
    active?: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                'w-full text-left px-2 py-1.5 rounded text-xs transition-colors',
                active
                    ? 'bg-signal-500/15 text-signal-700 dark:text-signal-300'
                    : 'text-graphite-800 dark:text-graphite-200 hover:bg-graphite-100 dark:hover:bg-graphite-800'
            )}
        >
            {children}
        </button>
    );
}
